import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { X } from "lucide-react";
import type { PartnerType } from "../PartnerContext";
import { MAX_KIOSKS_PER_REQUEST, parseKioskIds, registerKiosks, registrationError } from "../utils/kioskRegistration";
import common from "../style/screens/CommonV2.module.css";
import style from "../style/components/RegisterKiosksModal.module.css";

type Props = {
    partners: PartnerType[];
    initialPartnerId?: string;
    close: () => void;
    onRegistered: (partnerId: string, count: number) => void;
};

export default function RegisterKiosksModal({ partners, initialPartnerId, close, onRegistered }: Props) {
    const [partnerId, setPartnerId] = useState(() => partners.some(partner => partner.id === initialPartnerId) ? initialPartnerId! : "");
    const [text, setText] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const dialog = useRef<HTMLDivElement>(null);
    const partnerSelect = useRef<HTMLSelectElement>(null);
    const inFlight = useRef(false);
    const mounted = useRef(false);
    const closeRef = useRef(close);
    closeRef.current = close;
    const prefix = useId();
    const { ids, invalidIds, duplicates } = parseKioskIds(text);
    const validation = ids.length > MAX_KIOSKS_PER_REQUEST ? "한 번에 최대 50대까지 추가할 수 있습니다."
        : invalidIds.length ? `형식을 확인해주세요: ${invalidIds.join(", ")}`
        : duplicates.length ? `중복 입력된 ID가 있습니다: ${duplicates.join(", ")}` : "";
    const selectedPartner = partners.find(partner => partner.id === partnerId);

    useEffect(() => {
        mounted.current = true;
        const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const overflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        partnerSelect.current?.focus();
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape" && !inFlight.current) closeRef.current();
            if (event.key !== "Tab") return;
            const items = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled), select:not(:disabled), textarea:not(:disabled)') || []);
            const first = items[0];
            const last = items[items.length - 1];
            if (!first) { event.preventDefault(); dialog.current?.focus(); }
            else if (!items.includes(document.activeElement as HTMLElement)) { event.preventDefault(); first.focus(); }
            else if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => {
            mounted.current = false;
            document.removeEventListener("keydown", onKeyDown);
            document.body.style.overflow = overflow;
            previousFocus?.focus();
        };
    }, []);

    const submit = async (event: FormEvent) => {
        event.preventDefault();
        if (inFlight.current) return;
        if (!selectedPartner) { setError("파트너를 선택해주세요."); partnerSelect.current?.focus(); return; }
        if (!ids.length) { setError("추가할 기기 ID를 입력해주세요."); return; }
        if (validation) { setError(validation); return; }
        inFlight.current = true;
        setLoading(true);
        setError("");
        try {
            const result = await registerKiosks(partnerId, ids);
            if (mounted.current) onRegistered(partnerId, result.count);
        } catch (err) {
            if (mounted.current) setError(registrationError(err));
        } finally {
            inFlight.current = false;
            if (mounted.current) setLoading(false);
        }
    };

    return (
        <div className={[common["container--overlay"], common["container--overlay--active"]].join(" ")}
            onMouseDown={event => { if (event.target === event.currentTarget && !inFlight.current) close(); }}>
            <div ref={dialog} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={`${prefix}-title`} aria-describedby={`${prefix}-description`}
                className={[common["unit--overlay"], common["unit--overlay--active"], style.modal].join(" ")}>
                <form onSubmit={submit} aria-busy={loading} className={style.form}>
                    <div className={style.header}>
                        <div>
                            <h4 id={`${prefix}-title`}>기기 추가</h4>
                            <p id={`${prefix}-description`}>파트너를 선택하고 추가할 기기 ID를 입력해주세요.</p>
                        </div>
                        <button type="button" className={style.iconButton} aria-label="기기 추가 닫기" onClick={close} disabled={loading}><X size={18} /></button>
                    </div>
                    <div className={style.body}>
                        <div className={style.field}>
                            <label htmlFor={`${prefix}-partner`}>파트너</label>
                            <select ref={partnerSelect} id={`${prefix}-partner`} value={partnerId} disabled={loading}
                                onChange={event => { setPartnerId(event.target.value); setError(""); }}>
                                <option value="">파트너를 선택해주세요</option>
                                {partners.map(partner => <option key={partner.id} value={partner.id}>{partner.name} ({partner.id})</option>)}
                            </select>
                            {partners.length === 0 && <p className={style.help}>파트너 목록이 비어 있습니다. 목록이 로드되지 않으면 새로고침해주세요.</p>}
                        </div>
                        <div className={style.field}>
                            <div className={style.labelRow}><label htmlFor={`${prefix}-ids`}>기기 ID</label><span>{ids.length} / {MAX_KIOSKS_PER_REQUEST}대</span></div>
                            <textarea id={`${prefix}-ids`} rows={6} maxLength={12000} value={text} disabled={loading} spellCheck={false} autoCapitalize="none" autoCorrect="off"
                                aria-describedby={`${prefix}-help`} aria-invalid={Boolean(validation)} placeholder={"Cesco_17\nCesco_18\nCesco_19"}
                                onChange={event => { setText(event.target.value); setError(""); }} />
                            <p id={`${prefix}-help`} className={style.help}>줄바꿈이나 쉼표로 구분해주세요. 영문·숫자·밑줄(_)을 사용할 수 있으며, ID마다 최대 100자까지 입력할 수 있습니다. 대소문자는 구분합니다.</p>
                        </div>
                        <div className={style.notice}>추가한 기기는 등록 대기 상태로 저장됩니다. 목록에서 기본 정보를 입력하고 승인하면 활성화됩니다.</div>
                        {(validation || error) && <p role="alert" className={style.error}>{validation || error}</p>}
                    </div>
                    <div className={style.footer}>
                        <span>{selectedPartner ? `${selectedPartner.name} · ${ids.length}대` : "파트너 선택 필요"}</span>
                        <div>
                            <button type="button" className={style.button} onClick={close} disabled={loading}>취소</button>
                            <button type="submit" className={[style.button, style.primary].join(" ")} disabled={loading || !ids.length || !selectedPartner || Boolean(validation)}>
                                {loading ? "추가 중…" : `${ids.length || "기기"}${ids.length ? "대" : ""} 추가하기`}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}
